/* eslint-disable no-use-before-define */
import _ from 'underscore';
import { useContext, useMemo } from 'react';
import { ServerContext } from '@bingads-webui-campaign-react/shared-app-capi';

const EmptyObject = Object.freeze({});

export default function useFeatures(...features) {
  const [serverContext] = useContext(ServerContext) || [EmptyObject];
  // TODO: include more contexts (CCUI, etc.)
  const featuresCollection = useMemo(() => ({
    ...(serverContext.permissions && serverContext.permissions.dynamic) || EmptyObject,
    ...(serverContext.permissions && _.omit(serverContext.permissions, 'dynamic')) || EmptyObject,
    ...(serverContext.CCUIPilots && serverContext.CCUIPilots.dynamic) || EmptyObject,
    ...(serverContext.CCUIPilots && _.omit(serverContext.CCUIPilots, 'dynamic')) || EmptyObject,
  }), [serverContext]);

  // NOTE1: because we accept features to be specified on the fly, e.g. useFeatures('a', 'b', 'c'),
  // and read them through a rest parameter, we will always get a new array,
  // so to avoid unnecessary re-evaluations down the tree we compute a reversible hash of the array and use that as a dependency

  // NOTE2: because we want to accept the features to be specified both as an array and enumerated,
  // we have to defensively flatten the parameter to get the actual feature names,
  // e.g. when called like this useFeatures(['a', 'b', 'c']), "features" will be [['a', 'b', 'c']]
  const featuresHash = _.flatten(features).join();
  const evaluatedFeatures = useMemo(() => {
    const unpackedFeatures = featuresHash.split(',');
    const featureObjects = _.reduce(unpackedFeatures, (memo, f) => {
      // eslint-disable-next-line no-param-reassign
      memo[f] = getFeature(f, featuresCollection);
      return memo;
    }, {});

    return {
      values: _.map(unpackedFeatures, f => featureObjects[f].isEnabled),
      objects: featureObjects,
    };
  }, [featuresCollection, featuresHash]);

  return evaluatedFeatures;
}

function getFeature(feature, featuresCollection) {
  return {
    // TODO: add more metadata
    // NOTE: make sure the value is a boolean, as it might make a difference in some cases
    isEnabled: featuresCollection[feature] || featuresCollection[`is${feature}Enabled`] || false,
  };
}
